import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Route, Routes, Link, Navigate } from 'react-router-dom';
import { generateEncryptionKey, encryptFile } from './utils/crypto';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import HomeHeader from './components/HomeHeader';
import DownloadPage from './DownloadPage';
import ErrorPage from './ErrorPage';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import PortalSubmission from './pages/PortalSubmission';
import Pricing from './pages/Pricing';
import PortalBranding from './pages/PortalBranding';
import LandingPage from './pages/LandingPage';
import AccountSettings from './pages/AccountSettings';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Security from './pages/Security';
import './App.css';
import { v4 as uuidv4 } from 'uuid';
import { QRCodeCanvas } from 'qrcode.react';
import JSZip from 'jszip';
import srp from 'secure-remote-password/client';
import { FaClock, FaDownload, FaLock, FaEnvelope, FaLink, FaBan, FaInfoCircle, FaUpload, FaFileAlt, FaRegCopy, FaQrcode, FaHistory, FaTimes } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5005';
const accentBlue = "#2186EB";
const privacyOrange = "#FF7417";

const benefitList = [
  {
    icon: <FaLock className="text-blue-500" aria-hidden="true" />,
    title: "End-to-End Encryption",
    desc: "Your files are encrypted with AES-256 before upload. Only you and your recipient can decrypt.",
  },
  {
    icon: <FaClock className="text-blue-500" aria-hidden="true" />,
    title: "Self-Destruct & Expiry",
    desc: "Set files to auto-delete after a set time or number of downloads. No manual cleanup needed.",
  },
  {
    icon: <FaBan className="text-blue-500" aria-hidden="true" />,
    title: "No Tracking, No Logs",
    desc: "We never log, track, or analyze your files or metadata. Privacy is our default.",
  },
];

function Home() {
  const { isAuthenticated, user } = useAuth();

  // Plan-based limits
  const planLimits = {
    anonymous: { maxExpiry: 24, maxDownloads: 1, maxFileSizeMB: 20 },
    free: { maxExpiry: 24, maxDownloads: 5, maxFileSizeMB: 30 },
    pro: { maxExpiry: 720, maxDownloads: 100, maxFileSizeMB: 2048 },
    team: { maxExpiry: 2160, maxDownloads: 1000, maxFileSizeMB: 5120 }
  };

  const currentPlan = isAuthenticated ? (user?.plan || 'free') : 'anonymous';
  const limits = planLimits[currentPlan] || planLimits.anonymous;

  const allowedTypes = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
    'text/plain',
    'image/png',
    'image/jpeg'
  ];
  const maxFileSize = limits.maxFileSizeMB * 1024 * 1024;
  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [downloadLink, setDownloadLink] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [expirationHours, setExpirationHours] = useState(24); // default 24 hours
  const [maxDownloads, setMaxDownloads] = useState(1); // default one-time download
  const [password, setPassword] = useState('');
  const [fileError, setFileError] = useState('');
  const [showQR, setShowQR] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyWebhook, setNotifyWebhook] = useState('');
  const [showLog, setShowLog] = useState(false);
  const [accessLog, setAccessLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState('');
  const [srpSalt, setSrpSalt] = useState('');
  const [srpVerifier, setSrpVerifier] = useState('');
  const [csrfToken, setCsrfToken] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [toast, setToast] = useState(null);
  const [recentShares, setRecentShares] = useState([]);
  const [showRecentShares, setShowRecentShares] = useState(false);
  const fileInputRef = useRef();

  // Load recent shares from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('miragedocs_recent_shares');
      if (saved) {
        const shares = JSON.parse(saved);
        // Filter out expired shares (keep last 24 hours of shares)
        const validShares = shares.filter(s => {
          const shareTime = new Date(s.createdAt).getTime();
          const now = Date.now();
          return (now - shareTime) < 24 * 60 * 60 * 1000;
        });
        setRecentShares(validShares);
        // Update localStorage with filtered shares
        localStorage.setItem('miragedocs_recent_shares', JSON.stringify(validShares));
      }
    } catch (e) {
      console.error('Failed to load recent shares:', e);
    }
  }, []);

  // Save link to localStorage when upload is successful
  const saveToRecentShares = (link, filename) => {
    try {
      const newShare = {
        id: Date.now(),
        link,
        filename,
        createdAt: new Date().toISOString()
      };
      const updated = [newShare, ...recentShares].slice(0, 5); // Keep max 5 recent shares
      setRecentShares(updated);
      localStorage.setItem('miragedocs_recent_shares', JSON.stringify(updated));
    } catch (e) {
      console.error('Failed to save to recent shares:', e);
    }
  };

  // Clear a specific share from recent
  const clearRecentShare = (id) => {
    const updated = recentShares.filter(s => s.id !== id);
    setRecentShares(updated);
    localStorage.setItem('miragedocs_recent_shares', JSON.stringify(updated));
  };

  useEffect(() => {
    axios.get(`${API_URL}/csrf-token`).then(() => {
      const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
      if (match) setCsrfToken(match[1]);
    }).catch(() => {});
  }, []);

  const focusStyle = {
    outline: '2px solid #ff7417',
    outlineOffset: '2px',
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    // Validate all files
    for (const selectedFile of files) {
      if (!allowedTypes.includes(selectedFile.type)) {
        setFileError('Invalid file type. Allowed: PDF, DOCX, TXT, PNG, JPG');
        setFile(null);
        setSelectedFiles([]);
        return;
      }
      if (selectedFile.size > maxFileSize) {
        setFileError(`File too large. Maximum allowed size is ${limits.maxFileSizeMB}MB for ${currentPlan} plan.`);
        setFile(null);
        setSelectedFiles([]);
        return;
      }
    }
    setFileError('');
    setSelectedFiles(files);
    if (files.length === 1) {
      setFile(files[0]);
    } else {
      setFile(null); // We'll zip and set file in handleUpload
    }
  };

  // generateEncryptionKey and encryptFile are now imported from utils/crypto.js
  // They use AES-256-GCM (Web Crypto API) for authenticated encryption

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setUploadStatus('Please select file(s) first');
      return;
    }
    if (fileError) {
      setUploadStatus(fileError);
      return;
    }

    const key = generateEncryptionKey();
    // IV is now embedded in the encrypted data for AES-GCM, but we still send a placeholder for backend compatibility
    const ivArray = new Uint8Array(16);
    crypto.getRandomValues(ivArray);
    const iv = Array.from(ivArray, b => b.toString(16).padStart(2, '0')).join('');
    const fileId = uuidv4();
    const chunkIndex = 0;
    const totalChunks = 1;
    const publicKey = key; // Use encryption key as identifier
    const expiration = expirationHours * 60 * 60; // hours to seconds
    let uploadBlob, originalName;
    let salt = '', verifier = '';

    if (password) {
      salt = srp.generateSalt();
      verifier = srp.deriveVerifier(salt, fileId, password);
      setSrpSalt(salt);
      setSrpVerifier(verifier);
    }

    if (selectedFiles.length === 1) {
      // Single file, use as is
      const singleFile = selectedFiles[0];
      originalName = singleFile.name;
      setUploadStatus('Encrypting file...');
      const encryptedContent = await encryptFile(singleFile, key);
      uploadBlob = new Blob([encryptedContent], { type: 'text/plain' });
    } else {
      // Multiple files, zip first
      setUploadStatus('Zipping files...');
      const zip = new JSZip();
      for (const f of selectedFiles) {
        zip.file(f.name, f);
      }
      const zippedContent = await zip.generateAsync({ type: 'blob' });
      originalName = 'miragedocs-archive.zip';
      setUploadStatus('Encrypting zip...');
      const encryptedContent = await encryptFile(zippedContent, key);
      uploadBlob = new Blob([encryptedContent], { type: 'text/plain' });
    }

    try {
      setUploadStatus('Uploading...');
      const formData = new FormData();
      formData.append('chunk', uploadBlob, originalName);
      formData.append('fileId', fileId);
      formData.append('chunkIndex', chunkIndex);
      formData.append('totalChunks', totalChunks);
      formData.append('originalName', originalName);
      formData.append('key', key);
      formData.append('iv', iv);
      formData.append('publicKey', publicKey);
      formData.append('expiration', expiration);
      const safeMaxDownloads = Math.max(Number(maxDownloads) || 1, 1);
      formData.append('maxDownloads', safeMaxDownloads);
      formData.append('srpSalt', salt || '');
      formData.append('srpVerifier', verifier || '');
      if (notifyEmail) formData.append('notifyEmail', notifyEmail);
      if (notifyWebhook) formData.append('notifyWebhook', notifyWebhook);

      const response = await axios.post(`${API_URL}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-CSRF-Token': csrfToken
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });
      const token = btoa(`${response.data.fileId}:${key}`);
      // Fragment (#), not query (?) - the fragment is never sent to the server when
      // the link is opened, so it can't leak via access logs or the Referer header.
      const newLink = `${window.location.origin}/download#token=${encodeURIComponent(token)}`;
      setDownloadLink(newLink);
      setUploadStatus('Upload successful!');

      // Save to recent shares for link recovery
      saveToRecentShares(newLink, originalName);
      
    } catch (error) {
      setUploadStatus('Upload failed. Try again.');
      console.error('Upload error:', error);
    }
  };

  const handleViewLog = async () => {
    setShowLog(true);
    setLogLoading(true);
    setLogError('');
    try {
      const rawToken = downloadLink.match(/token=([^&]+)/)?.[1];
      const token = rawToken ? decodeURIComponent(rawToken) : btoa(`${downloadLink.split('/download/')[1]}:${password}`);
      // POST, not GET, so the key-bearing token never lands in a URL/access log.
      const response = await axios.post(`${API_URL}/access-log`, { token });
      setAccessLog(response.data.log || []);
    } catch (err) {
      setLogError('Could not fetch access log. The file may have expired or been deleted.');
      setAccessLog([]);
    }
    setLogLoading(false);
  };

  // Toast helper
  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Drag & drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange({ target: { files: e.dataTransfer.files } });
    }
  };

  return (
    <div
      className="min-h-screen bg-gray-50 flex flex-col"
      style={{
        "--accent-blue": accentBlue,
        "--privacy-orange": privacyOrange,
        "--bg-main": "#f8f9fa",
        "--text-main": "#222",
        "--card-bg": "#fff",
        "--muted": "#6b7280",
      }}
    >
      {/* Header */}
      <header className="w-full py-6 px-4 bg-white border-b border-gray-200 flex items-center justify-between">
        <Link to={isAuthenticated ? "/dashboard" : "/"} className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FaLock className="text-[var(--accent-blue)]" aria-hidden="true" />
          MirageDocs
        </Link>
        <div className="flex items-center gap-4">
          {isAuthenticated ? (
            <>
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                {currentPlan.toUpperCase()}
              </span>
              <Link to="/dashboard" className="px-4 py-2 bg-[var(--accent-blue)] text-white rounded-lg hover:bg-blue-700 transition">
                Dashboard
              </Link>
            </>
          ) : (
            <>
              <Link to="/login" className="text-gray-700 hover:text-[var(--accent-blue)] transition">
                Sign In
              </Link>
              <Link to="/register" className="px-4 py-2 bg-[var(--privacy-orange)] text-white rounded-lg hover:bg-orange-600 transition">
                Sign Up
              </Link>
            </>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col md:flex-row items-center justify-center gap-10 px-4 py-10 bg-[var(--bg-main)]">
        {/* Left: Benefits */}
        <section className="w-full max-w-md mb-8 md:mb-0 md:w-1/2">
          <div className="bg-[var(--card-bg)] rounded-2xl shadow-md p-8 flex flex-col gap-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <FaInfoCircle className="text-[var(--accent-blue)]" aria-hidden="true" />
              How MirageDocs Works
            </h2>
            <ul className="flex flex-col gap-5">
              {benefitList.map((b, i) => (
                <li key={i} className="flex items-start gap-4">
                  <span className="text-2xl mt-1">{b.icon}</span>
                  <div>
                    <div className="font-semibold text-gray-800">{b.title}</div>
                    <div className="text-[var(--muted)] text-sm">{b.desc}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Right: Upload Form */}
        <section className="w-full max-w-lg md:w-1/2">
          <form
            className="bg-[var(--card-bg)] rounded-2xl shadow-lg p-8 flex flex-col gap-6"
            onSubmit={e => { e.preventDefault(); handleUpload(); }}
            aria-label="Secure file upload form"
          >
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2 mb-1">
                Start Sharing Securely
                <FaLock className="text-[var(--accent-blue)]" aria-hidden="true" />
              </h1>
              <div className="text-sm text-[var(--muted)] mb-2">
                Upload and share encrypted files with privacy-first security.
              </div>
              <div className="text-xs text-[var(--muted)] mb-2">
                AES-256 Encrypted. Zero Logs.
              </div>
            </div>

            {/* Anonymous user banner */}
            {!isAuthenticated && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                <span className="text-blue-800">
                  <strong>Guest mode:</strong> {limits.maxFileSizeMB}MB max, {limits.maxExpiry}h expiry, {limits.maxDownloads} download.{' '}
                  <Link to="/register" className="text-[var(--accent-blue)] font-semibold hover:underline">
                    Sign up free
                  </Link>{' '}
                  for more!
                </span>
              </div>
            )}

            {/* Drag & Drop Zone */}
            <div
              className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 transition-colors cursor-pointer ${
                dragActive
                  ? "border-[var(--accent-blue)] bg-blue-50"
                  : "border-gray-300 bg-gray-100"
              }`}
              onClick={() => document.getElementById('fileInput').click()}
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              tabIndex={0}
              aria-label="File upload drop zone"
            >
              <FaFileAlt className="text-3xl text-[var(--accent-blue)] mb-2" aria-hidden="true" />
              <span className="font-medium text-gray-700">
                {selectedFiles.length > 0 ? (
                  <span className="text-[var(--privacy-orange)]">{selectedFiles[0].name}</span>
                ) : (
                  <span className="text-[var(--muted)]">Drag & drop or click to select a file</span>
                )}
              </span>
              <input
                id="fileInput"
                type="file"
                className="hidden"
                onChange={handleFileChange}
                aria-label="Select file to upload"
              />
            </div>

            {/* Fields */}
            <div className="flex flex-col gap-4">
              {/* Expiration */}
              <div>
                <label htmlFor="expiration" className="font-semibold text-gray-800 flex items-center gap-2">
                  <FaClock className="text-[var(--accent-blue)]" aria-hidden="true" />
                  Expiration (hours)
                </label>
                <input
                  id="expiration"
                  type="number"
                  min="1"
                  max={limits.maxExpiry}
                  value={Math.min(expirationHours, limits.maxExpiry)}
                  onChange={e => setExpirationHours(Math.min(Number(e.target.value), limits.maxExpiry))}
                  className="w-full mt-1 rounded-md border border-gray-300 p-3 focus:ring-2 focus:ring-[var(--accent-blue)] transition text-base"
                  aria-label="Expiration in hours"
                />
                <div className="text-xs text-[var(--muted)] mt-1">
                  Max {limits.maxExpiry}h for {currentPlan} plan.
                  {currentPlan === 'free' && <Link to="/pricing" className="ml-1 text-[var(--accent-blue)]">Upgrade for more</Link>}
                </div>
              </div>
              {/* Max Downloads */}
              <div>
                <label htmlFor="maxDownloads" className="font-semibold text-gray-800 flex items-center gap-2">
                  <FaBan className="text-[var(--accent-blue)]" aria-hidden="true" />
                  Max Downloads
                </label>
                <input
                  id="maxDownloads"
                  type="number"
                  min="1"
                  max={limits.maxDownloads}
                  value={Math.min(maxDownloads, limits.maxDownloads)}
                  onChange={e => setMaxDownloads(Math.min(Number(e.target.value) || 1, limits.maxDownloads))}
                  className="w-full mt-1 rounded-md border border-gray-300 p-3 focus:ring-2 focus:ring-[var(--accent-blue)] transition text-base"
                  aria-label="Max downloads"
                />
                <div className="text-xs text-[var(--muted)] mt-1">
                  Max {limits.maxDownloads} downloads for {currentPlan} plan.
                </div>
              </div>
              {/* Password */}
              <div>
                <label htmlFor="password" className="font-semibold text-gray-800 flex items-center gap-2">
                  <FaLock className="text-[var(--accent-blue)]" aria-hidden="true" />
                  Password (optional)
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full mt-1 rounded-md border border-gray-300 p-3 focus:ring-2 focus:ring-[var(--accent-blue)] transition text-base"
                  aria-label="Password (optional)"
                />
                <div className="text-xs text-[var(--muted)] mt-1">
                  Add a password to limit access. Only those with the password can decrypt.
                </div>
              </div>
              {/* Notify Email */}
              <div>
                <label htmlFor="notifyEmail" className="font-semibold text-gray-800 flex items-center gap-2">
                  <FaEnvelope className="text-[var(--accent-blue)]" aria-hidden="true" />
                  Email notifications (optional)
                </label>
                <input
                  id="notifyEmail"
                  type="email"
                  placeholder="your@email.com"
                  value={notifyEmail}
                  onChange={e => setNotifyEmail(e.target.value)}
                  className="w-full mt-1 rounded-md border border-gray-300 p-3 focus:ring-2 focus:ring-[var(--accent-blue)] transition text-base"
                  aria-label="Email for download and deletion notifications"
                />
                <div className="text-xs text-[var(--muted)] mt-1">
                  Get notified when your file is downloaded or deleted.
                </div>
              </div>
              {/* Webhook */}
              <div>
                <label htmlFor="notifyWebhook" className="font-semibold text-gray-800 flex items-center gap-2">
                  <FaLink className="text-[var(--accent-blue)]" aria-hidden="true" />
                  Webhook URL (optional)
                </label>
                <input
                  id="notifyWebhook"
                  type="url"
                  placeholder="https://your-webhook-url"
                  value={notifyWebhook}
                  onChange={e => setNotifyWebhook(e.target.value)}
                  className="w-full mt-1 rounded-md border border-gray-300 p-3 focus:ring-2 focus:ring-[var(--accent-blue)] transition text-base"
                  aria-label="Webhook URL for self-destruct notification"
                />
                <div className="text-xs text-[var(--muted)] mt-1">
                  Receive a webhook when your file self-destructs.
                </div>
              </div>
            </div>

            {/* Upload Button */}
            <button
              type="submit"
              className="w-full py-4 mt-2 rounded-lg font-bold text-lg bg-[var(--privacy-orange)] text-white shadow transition hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400"
              aria-label="Upload file"
            >
              <span className="flex items-center justify-center gap-2">
                <FaUpload className="text-xl" aria-hidden="true" />
                Upload
              </span>
            </button>
            {uploadStatus && (
              <div className="text-center text-[var(--privacy-orange)] font-semibold mt-2">{uploadStatus}</div>
            )}
            {fileError && (
              <div className="text-center text-red-600 font-semibold mt-2">{fileError}</div>
            )}
            {downloadLink && (
              <div className="mt-6 flex flex-col items-center gap-2">
                <label className="font-semibold text-gray-700 mb-1">Shareable Link:</label>
                <div className="flex gap-2 w-full items-center">
                  <input
                    type="text"
                    readOnly
                    value={downloadLink}
                    className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm bg-gray-100"
                    aria-label="Shareable download link"
                    onFocus={e => e.target.select()}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(downloadLink);
                      showToast('Link copied!', 'success');
                    }}
                    className="p-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700"
                    aria-label="Copy download link to clipboard"
                  >
                    <FaRegCopy className="text-lg" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowQR(!showQR)}
                    className="p-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700"
                    aria-label={showQR ? 'Hide QR Code' : 'Show QR Code'}
                  >
                    <FaQrcode className="text-lg" />
                  </button>
                </div>
                {showQR && (
                  <div className="mt-4 flex flex-col items-center">
                    <QRCodeCanvas value={downloadLink} size={180} includeMargin={true} aria-label="QR code for download link" />
                    <button
                      onClick={() => {
                        const canvas = document.querySelector('canvas');
                        const url = canvas.toDataURL('image/png');
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'miragedocs-qr.png';
                        a.click();
                      }}
                      className="mt-2 px-3 py-1 rounded bg-[var(--accent-blue)] text-white font-semibold hover:bg-blue-700 transition"
                      aria-label="Download QR code as PNG"
                    >
                      Download QR Code
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Recent Shares - Link Recovery */}
            {recentShares.length > 0 && (
              <div className="mt-6 border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={() => setShowRecentShares(!showRecentShares)}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition text-sm font-medium"
                >
                  <FaHistory />
                  Recent Shares ({recentShares.length})
                  <span className={`transform transition ${showRecentShares ? 'rotate-180' : ''}`}>
                    ▼
                  </span>
                </button>
                {showRecentShares && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs text-gray-500 mb-2">
                      Links are saved locally for 24 hours in case you need to recover them.
                    </p>
                    {recentShares.map(share => (
                      <div key={share.id} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-800 truncate">{share.filename}</div>
                          <div className="text-xs text-gray-500">
                            {new Date(share.createdAt).toLocaleString()}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(share.link);
                            showToast('Link copied!', 'success');
                          }}
                          className="p-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700"
                          title="Copy link"
                        >
                          <FaRegCopy />
                        </button>
                        <button
                          type="button"
                          onClick={() => clearRecentShare(share.id)}
                          className="p-2 rounded bg-red-100 hover:bg-red-200 text-red-600"
                          title="Remove from history"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </form>
        </section>
      </main>

      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed bottom-6 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-lg shadow-lg z-50 text-white font-semibold transition-all ${
            toast.type === "success"
              ? "bg-[var(--accent-blue)]"
              : "bg-red-600"
          }`}
          role="alert"
        >
          {toast.msg}
        </div>
      )}

      {/* Footer */}
      <footer className="w-full py-6 bg-gray-900 text-gray-400 mt-auto">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2 text-white">
              <FaLock />
              <span className="font-bold">MirageDocs</span>
            </div>
            <div className="flex gap-6 text-sm">
              <Link to="/privacy" className="hover:text-white transition">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition">Terms</Link>
              <Link to="/security" className="hover:text-white transition">Security</Link>
              <Link to="/pricing" className="hover:text-white transition">Pricing</Link>
            </div>
            <div className="text-sm">
              Privacy First | Zero Logs
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Smart home route - shows landing for guests, redirects to dashboard for authenticated users
function HomeRoute() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />;
}

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/upload" element={<Home />} />
          <Route path="/download" element={<DownloadPage />} />
          <Route path="/error" element={<ErrorPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/security" element={<Security />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/portals/:portalId/branding"
            element={
              <ProtectedRoute>
                <PortalBranding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <AccountSettings />
              </ProtectedRoute>
            }
          />
          <Route path="/receive/:portalId" element={<PortalSubmission />} />
        </Routes>
      </Router>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
